// ═══════════════════════════════════════════════════════════════════════════
// srtEditor.js — Funções puras do Editor de SRT Tempo (sem React)
//
// Modelo: o worker de áudio salva no R2 um words_time.json com TODAS as
// palavras narradas e seus timestamps reais ([{word, start_time, end_time}]
// em ms). Cada bloco de um SRT é apenas um agrupamento de palavras
// consecutivas: start do bloco = start da 1ª palavra, end = end da última.
// O editor portanto trabalha com a lista IMUTÁVEL de palavras + índices de
// quebra (breaks). Editar = mover quebras; os tempos são sempre recalculados
// dos timestamps reais das palavras — sincronização fiel por construção.
//
// O SRT existente (srtsynctempo.srt) é parseado e ALINHADO às palavras por
// timestamp (o gerador usa alvo aleatório por bloco, então não dá para
// regerar e bater com o arquivo — o arquivo é a fonte dos blocos iniciais).
// ═══════════════════════════════════════════════════════════════════════════

const SRT_TIME_RE = /^(\d{2,}):(\d{2}):(\d{2})[,.](\d{3})\s*-->\s*(\d{2,}):(\d{2}):(\d{2})[,.](\d{3})/;

// Pontuação que o gerador (reconstruirTexto do worker) gruda sem espaço
const PUNCT_ONLY = new Set(['.', ',', '!', '?', ';', ':', '-']);

const isPunctOnly = (s) => {
  const t = (s || '').trim();
  return t.length === 1 && PUNCT_ONLY.has(t);
};

/**
 * Converte a URL de um arquivo SRT do CDN para a URL do words_time.json
 * do mesmo job (mesmo prefixo /audios/{job_id}/).
 */
export function wordsUrlFromSrtUrl(srtUrl) {
  if (!srtUrl) return null;
  return srtUrl.replace(/\/[^/]+(\?.*)?$/, '/words_time.json');
}

/**
 * Formata milissegundos no timecode SRT: HH:MM:SS,mmm
 */
export function formatSrtTime(ms) {
  const total = Math.max(0, Math.round(ms));
  const msPart = total % 1000;
  const totalSec = Math.floor(total / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n, l = 2) => String(n).padStart(l, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(msPart, 3)}`;
}

/**
 * Parseia o conteúdo de um arquivo SRT.
 * @returns {Array<{index:number, startMs:number, endMs:number, text:string}>}
 */
export function parseSrt(content) {
  if (typeof content !== 'string') throw new Error('srt_parse');
  const noBom = content.charCodeAt(0) === 0xfeff ? content.slice(1) : content;
  const clean = noBom.replace(/\r\n?/g, '\n').trim();
  if (!clean) throw new Error('srt_parse');

  const blocks = [];
  for (const raw of clean.split(/\n{2,}/)) {
    const lines = raw.split('\n').map((l) => l.trim()).filter((l) => l !== '');
    if (lines.length === 0) continue;
    // Linha 1 = número do bloco (opcional em SRTs malformados), linha 2 = timecode
    let cursor = 0;
    if (/^\d+$/.test(lines[0])) cursor = 1;
    const m = SRT_TIME_RE.exec(lines[cursor] || '');
    if (!m) throw new Error('srt_parse');
    const toMs = (h, mi, s, mm) => (+h) * 3600000 + (+mi) * 60000 + (+s) * 1000 + (+mm);
    const text = lines.slice(cursor + 1).join(' ').trim();
    blocks.push({
      index: blocks.length + 1,
      startMs: toMs(m[1], m[2], m[3], m[4]),
      endMs: toMs(m[5], m[6], m[7], m[8]),
      text,
    });
  }
  if (blocks.length === 0) throw new Error('srt_parse');
  return blocks;
}

/**
 * Normaliza o words_time.json para [{word, startMs, endMs}].
 * Formato confirmado do worker: [{word, start_time, end_time}] em ms.
 * Defensivo: aceita também [{word|text, start, end}] em segundos (float).
 */
export function normalizeWords(json) {
  if (!Array.isArray(json) || json.length === 0) throw new Error('words_format');
  const out = [];
  for (const item of json) {
    if (!item || typeof item !== 'object') continue;
    const word = String(item.word ?? item.text ?? '').trim();
    if (!word) continue;
    let startMs;
    let endMs;
    if (item.start_time !== undefined && item.end_time !== undefined) {
      startMs = Math.round(Number(item.start_time));
      endMs = Math.round(Number(item.end_time));
    } else if (item.start !== undefined && item.end !== undefined) {
      startMs = Math.round(Number(item.start) * 1000);
      endMs = Math.round(Number(item.end) * 1000);
    } else {
      throw new Error('words_format');
    }
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) throw new Error('words_format');
    out.push({ word, startMs, endMs });
  }
  if (out.length === 0) throw new Error('words_format');
  // Sanidade: timestamps devem ser (quase) monotônicos
  for (let i = 1; i < out.length; i++) {
    if (out[i].startMs < out[i - 1].startMs - 5) throw new Error('words_format');
  }
  return out;
}

/**
 * Detecta a transformação de case aplicada no SRT em relação às palavras
 * originais ('upper' | 'lower' | 'normal').
 */
function detectCase(blocks, words) {
  const srtText = blocks.map((b) => b.text).join(' ');
  const wordsText = words.map((w) => w.word).join(' ');
  if (srtText === wordsText) return 'normal';
  const lc = (s) => s.toLocaleLowerCase();
  if (lc(srtText) === lc(wordsText)) {
    if (srtText === srtText.toLocaleUpperCase()) return 'upper';
    if (srtText === srtText.toLocaleLowerCase()) return 'lower';
  }
  return 'normal';
}

const applyCase = (word, mode) =>
  mode === 'upper' ? word.toLocaleUpperCase() : mode === 'lower' ? word.toLocaleLowerCase() : word;

/**
 * Alinha os blocos do SRT às palavras do words_time.json.
 *
 * Matching sequencial por TIMESTAMP (tolerância ±60ms — cobre arredondamento
 * de ms do worker); fallback por contagem de tokens do texto do bloco com
 * validação do end (±200ms). Nunca alinha primariamente por texto: o SRT pode
 * estar em caixa alta/baixa enquanto o words_time.json está em case normal.
 *
 * @returns {{ breaks:number[], displayWords:string[] }}
 *   breaks — índices globais de palavra onde cada bloco k>=1 começa
 *   displayWords — texto de exibição de cada palavra, preservando o case do
 *   arquivo (token do SRT quando o mapeamento é 1:1; senão case detectado)
 */
export function alignSrtToWords(blocks, words) {
  const TOL = 60;
  const END_TOL = 200;
  const caseMode = detectCase(blocks, words);
  const breaks = [];
  const displayWords = new Array(words.length);
  let w = 0;

  for (let b = 0; b < blocks.length; b++) {
    const block = blocks[b];
    if (w >= words.length) throw new Error('align');
    const startIdx = w;

    // Início do bloco deve casar com a palavra corrente
    if (Math.abs(words[startIdx].startMs - block.startMs) > TOL) throw new Error('align');

    // A fronteira é o START do PRÓXIMO bloco (= start exato da 1ª palavra
    // dele). Decidir pelo start evita engolir palavras curtas do bloco
    // seguinte, o que acontecia ao consumir por end com tolerância.
    let end; // índice exclusivo da última palavra do bloco
    if (b < blocks.length - 1) {
      const target = blocks[b + 1].startMs;
      let best = -1;
      let bestDist = Infinity;
      for (let j = startIdx + 1; j < words.length; j++) {
        const d = Math.abs(words[j].startMs - target);
        if (d < bestDist) { best = j; bestDist = d; }
        if (words[j].startMs > target + TOL) break;
      }
      if (best < 0 || bestDist > TOL) {
        // Fallback: contagem de tokens do texto do bloco
        const n = block.text ? block.text.split(/\s+/).length : 0;
        if (n <= 0 || startIdx + n >= words.length) throw new Error('align');
        best = startIdx + n;
      }
      end = best;
    } else {
      end = words.length;
    }

    // Sanidade: fim do bloco ≈ end da última palavra consumida
    if (Math.abs(words[end - 1].endMs - block.endMs) > END_TOL) throw new Error('align');

    // Texto de exibição: se os tokens do bloco casam 1:1 com as palavras,
    // usa os tokens do arquivo (preserva byte-a-byte); senão aplica o case
    // detectado sobre as palavras originais.
    const count = end - startIdx;
    const tokens = block.text ? block.text.split(/\s+/) : [];
    for (let k = 0; k < count; k++) {
      displayWords[startIdx + k] =
        tokens.length === count ? tokens[k] : applyCase(words[startIdx + k].word, caseMode);
    }

    w = end;
    if (b < blocks.length - 1) breaks.push(end);
  }

  if (w !== words.length) throw new Error('align');
  return { breaks, displayWords };
}

/**
 * Deriva os blocos a partir das palavras + índices de quebra.
 * Tempos SEMPRE derivados dos timestamps reais das palavras — gaps de
 * silêncio preservados e duração nunca excede o áudio, por construção.
 *
 * @param {Array<{word,startMs,endMs}>} words
 * @param {number[]} breaks índices crescentes em (0, words.length)
 * @param {string[]} displayWords
 * @returns {Array<{startMs,endMs,wordStart,wordEnd,text}>}
 */
export function blocksFromBreaks(words, breaks, displayWords) {
  const bounds = [0, ...breaks, words.length];
  const blocks = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    const from = bounds[i];
    const to = bounds[i + 1];
    if (to <= from) continue;
    // Junta como o gerador original: pontuação isolada gruda sem espaço
    let text = '';
    for (let j = from; j < to; j++) {
      const dw = displayWords ? displayWords[j] : words[j].word;
      if (text !== '' && !isPunctOnly(dw)) text += ' ';
      text += dw;
    }
    blocks.push({
      startMs: words[from].startMs,
      endMs: words[to - 1].endMs,
      wordStart: from,
      wordEnd: to,
      text: text.trim(),
    });
  }
  return blocks;
}

/**
 * Classifica a pontuação final de uma palavra (espelha classePontuacao do
 * gerador do worker): 'strong' = fim de frase (. ! ? …), 'soft' = pausa
 * (, ; :), 'none'. Tolera 1 fecha-aspas/parêntese no final (curvo ou reto).
 */
export function punctClass(word) {
  let t = (word || '').trim();
  if (!t) return 'none';
  let last = t.slice(-1);
  if (['”', '’', ')', ']', '"', "'"].includes(last)) {
    t = t.slice(0, -1);
    last = t.slice(-1);
  }
  if (!last) return 'none';
  if (['.', '!', '?', '…'].includes(last)) return 'strong';
  if ([',', ';', ':'].includes(last)) return 'soft';
  return 'none';
}

/**
 * Quebra inteligente com REGRAS por grupo de blocos: cada regra é
 * `{ count, minMs, maxMs }` e vale para os próximos `count` blocos gerados
 * (a ÚLTIMA regra vale para todo o restante — count é ignorado nela).
 * Ex.: [{count: 5, minMs: 3000, maxMs: 5000}, {minMs: 6000, maxMs: 8000}]
 * → 5 primeiros blocos entre 3-5s, demais entre 6-8s.
 *
 * Cada bloco termina em fim de frase (pontuação forte) sempre que possível,
 * sem ultrapassar maxMs. O corte nunca acontece antes de o bloco alcançar
 * minMs (evita bloco de uma palavra solta) — exceto quando o texto restante
 * é menor que o mínimo. Se o ÚLTIMO bloco ficar abaixo do mínimo, ele é
 * fundido com o bloco anterior. Como só devolve índices de quebra sobre as
 * palavras reais, o sync nunca muda.
 *
 * @param {Array<{word,startMs,endMs}>} words
 * @param {Array<{count?:number,minMs:number,maxMs:number}>} rules
 * @returns {number[]} breaks
 */
export function smartBreaksRanged(words, rules) {
  const n = words.length;
  const breaks = [];
  if (!Array.isArray(rules) || rules.length === 0) return breaks;

  // Regra do bloco k: consome os counts na ordem; a última regra é infinita
  const ruleFor = (k) => {
    let acc = 0;
    for (let r = 0; r < rules.length - 1; r++) {
      acc += Math.max(0, rules[r].count || 0);
      if (k < acc) return rules[r];
    }
    return rules[rules.length - 1];
  };

  let start = 0;
  let blockIdx = 0;
  while (start < n) {
    const rule = ruleFor(blockIdx);
    const maxMs = Math.max(1, rule.maxMs || 0);
    const minMs = Math.max(0, Math.min(rule.minMs || 0, maxMs));

    // Última palavra que ainda cabe no teto a partir de start
    let limit = start;
    while (limit + 1 < n && words[limit + 1].endMs - words[start].startMs <= maxMs) limit++;
    if (limit >= n - 1) {
      // O restante vira o último bloco. Se ficou abaixo do mínimo, funde com
      // o bloco de cima — mas só quando o bloco fundido não estoura o teto,
      // OU quando o final é uma única palavra solta (pior caso visual).
      if (breaks.length > 0 && words[n - 1].endMs - words[start].startMs < minMs) {
        const prevStart = breaks.length >= 2 ? breaks[breaks.length - 2] : 0;
        const mergedDur = words[n - 1].endMs - words[prevStart].startMs;
        if (n - start === 1 || mergedDur <= maxMs) breaks.pop();
      }
      break;
    }

    // Piso: primeira palavra em que o bloco já alcança o mínimo
    let floor = start;
    while (floor < limit && words[floor].endMs - words[start].startMs < minMs) floor++;

    // Preferência: fim de frase mais distante em [floor, limit] > última pausa > teto
    let cut = -1;
    for (let j = limit; j >= floor; j--) {
      if (punctClass(words[j].word) === 'strong') { cut = j; break; }
    }
    if (cut < 0) {
      for (let j = limit; j >= floor; j--) {
        if (punctClass(words[j].word) === 'soft') { cut = j; break; }
      }
    }
    if (cut < 0) cut = limit;

    breaks.push(cut + 1);
    start = cut + 1;
    blockIdx++;
  }
  return breaks;
}

/**
 * Quebra inteligente simples (compat): só teto de duração, sem mínimo.
 * Equivale a smartBreaksRanged com uma única regra {minMs: 0, maxMs}.
 *
 * @param {Array<{word,startMs,endMs}>} words
 * @param {number} maxMs teto de duração por bloco (ms)
 * @returns {number[]} breaks
 */
export function smartBreaks(words, maxMs) {
  return smartBreaksRanged(words, [{ minMs: 0, maxMs }]);
}

/**
 * Gera o conteúdo do arquivo SRT a partir dos blocos derivados.
 */
export function buildSrt(blocks) {
  let srt = '';
  let n = 1;
  for (const b of blocks) {
    if (!b.text) continue;
    srt += `${n}\n${formatSrtTime(b.startMs)} --> ${formatSrtTime(b.endMs)}\n${b.text}\n\n`;
    n++;
  }
  return srt;
}
